import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  CssBaseline,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { Weekly } from "./Weekly";
import { Monthly } from "./Monthly";
import { ShiftSummaryPanel } from "./ShiftSummaryPanel";
import {
  shiftPalette,
  shiftCardSx,
  LEGEND,
  summarizeShifts,
} from "./shiftHelpers";
import {
  designColors,
  designFontFamily,
  designTheme,
  designType,
  designWeights,
} from "services/constants/designTokens";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";

const WEEK_FMT = "GGGG-[W]WW";
const MONTH_DATE_FMT = "YYYY-MM-DD";

const getInitialMonthlyRange = () => {
  const start = moment().startOf("month");
  const end = moment().endOf("month");
  return {
    start: start.format(MONTH_DATE_FMT),
    end: end.format(MONTH_DATE_FMT),
    label: "This Month",
  };
};

const resolveMonthlyRangeLabel = (start, end, fallbackLabel) => {
  const thisMonthStart = moment().startOf("month");
  const thisMonthEnd = moment().endOf("month");
  const lastMonthStart = moment().subtract(1, "month").startOf("month");
  const lastMonthEnd = moment().subtract(1, "month").endOf("month");

  if (start.isSame(thisMonthStart, "day") && end.isSame(thisMonthEnd, "day")) {
    return "This Month";
  }
  if (start.isSame(lastMonthStart, "day") && end.isSame(lastMonthEnd, "day")) {
    return "Last Month";
  }
  return ["Today", "Yesterday", "Last 7 Days", "Last 30 Days"].includes(fallbackLabel)
    ? fallbackLabel
    : "Custom Range";
};

const formatMonthlyPeriodLabel = ({ start, end, label }) => {
  const startMoment = moment(start, MONTH_DATE_FMT, true);
  const endMoment = moment(end, MONTH_DATE_FMT, true);

  if (!startMoment.isValid() || !endMoment.isValid()) return "";
  if (label === "This Month" || label === "Last Month") {
    return startMoment.format("MMMM YYYY");
  }
  if (startMoment.isSame(endMoment, "day")) {
    return startMoment.format("DD MMM YYYY");
  }
  return `${startMoment.format("DD MMM YYYY")} - ${endMoment.format("DD MMM YYYY")}`;
};

// Redesigned My Zone > Shift screen (image 2). The data-fetching (leave-year
// window, daily_user_shift_assignments) is unchanged from the previous version;
// only the presentation — header, view toggle, month/week navigation, calendar
// grid, legend and the Shift Summary side panel — was rebuilt.
const ShiftContent = () => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const [dataVisualMethod, setDataVisualMethod] = useState("Daily");
  const { domain, token, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [monthlyRange, setMonthlyRange] = useState(getInitialMonthlyRange);
  const [loading, setIsLoading] = useState(false);
  const [fetchWeekly, setFetchWeekly] = useState(false);
  const [weeklyShiftList, setWeeklyShiftList] = useState([]);
  const [listOfDate, setListOfDate] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const minDate = new Date(2020, 0, 1);
  const [selectedDay, setSelectedDay] = useState(() =>
    moment().format(MONTH_DATE_FMT),
  );

  const getCurrentWeek = () => moment().format(WEEK_FMT);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek);

  const selectView = (view) => {
    setDataVisualMethod(view);
    dispatch(setGlobalSearchValue(""));
    if (view === "Weekly") setSelectedWeek(getCurrentWeek());
    if (view === "Daily") setSelectedDay(moment().format(MONTH_DATE_FMT));
  };

  const handleDailyDateOnChange = (event) => setSelectedDay(event.target.value);

  const handleWeeklyDateOnChange = (event) => setSelectedWeek(event.target.value);

  const handleMonthlyDateOnChange = (rangeStart, rangeEnd, rangeLabel) => {
    const start = moment(rangeStart);
    const end = moment(rangeEnd);

    if (!start.isValid() || !end.isValid()) return;
    if (start.isBefore(moment(minDate), "day")) {
      showToast("error", "Year must be 01/01/2020 or later");
      return;
    }

    setMonthlyRange({
      start: start.format(MONTH_DATE_FMT),
      end: end.format(MONTH_DATE_FMT),
      label: resolveMonthlyRangeLabel(start, end, rangeLabel),
    });
  };

  const stepPeriod = (dir) => {
    if (dataVisualMethod === "Daily") {
      setSelectedDay((prev) =>
        moment(prev, MONTH_DATE_FMT).add(dir, "day").format(MONTH_DATE_FMT),
      );
      return;
    }
    setSelectedWeek((prev) =>
      moment(prev, WEEK_FMT).add(dir, "week").format(WEEK_FMT),
    );
  };

  const getWeekDays = (start) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getRangeDays = (start, end) => {
    const days = [];
    const cur = moment(start, MONTH_DATE_FMT, true);
    const lastDay = moment(end, MONTH_DATE_FMT, true);

    while (cur.isValid() && lastDay.isValid() && cur.isSameOrBefore(lastDay, "day")) {
      days.push(cur.format(MONTH_DATE_FMT));
      cur.add(1, "day");
    }
    return days;
  };

  useEffect(() => {
    if (selectedWeek && dataVisualMethod) {
      if (dataVisualMethod === "Weekly") {
        const start = moment(selectedWeek, WEEK_FMT).startOf("isoWeek").toDate();
        const end = moment(selectedWeek, WEEK_FMT).endOf("isoWeek").toDate();
        const formatDates = getWeekDays(start).map((v) =>
          moment(v).format("YYYY-MM-DD"),
        );
        setListOfDate(formatDates);
        setStartDate(moment(start).format("YYYY-MM-DD"));
        setEndDate(moment(end).format("YYYY-MM-DD"));
        setFetchWeekly(true);
      } else if (dataVisualMethod === "Daily") {
        setListOfDate([selectedDay]);
        setStartDate(selectedDay);
        setEndDate(selectedDay);
        setFetchWeekly(true);
      } else {
        const formatDates = getRangeDays(monthlyRange.start, monthlyRange.end);
        setListOfDate(formatDates);
        setStartDate(monthlyRange.start);
        setEndDate(monthlyRange.end);
        setFetchWeekly(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataVisualMethod,
    selectedDay,
    selectedWeek,
    monthlyRange.start,
    monthlyRange.end,
  ]);

  useEffect(() => {
    const fetchShiftList = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${domain}daily_user_shift_assignments/?selected_user_id=${userDetails?.id}&from_date=${startDate}&to_date=${endDate}&company_id=${requestCompanyId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          if (dataVisualMethod === "Monthly") {
            setWeeklyShiftList([]);
            setMonthlyData(res?.data?.[0]?.shifts || []);
          } else {
            setMonthlyData([]);
            setWeeklyShiftList(res?.data || []);
          }
          setIsLoading(false);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[MyShift] operation failed:", error);
        showToast("error", "Couldn't load your shift details. Please refresh the page.");
        setIsLoading(false);
      }
    };
    if (fetchWeekly && startDate !== "" && endDate !== "" && userDetails?.id) {
      fetchShiftList();
      setFetchWeekly(false);
    }
  }, [domain, endDate, fetchWeekly, startDate, token, userDetails?.id, dataVisualMethod, requestCompanyId]);

  const summary = useMemo(() => {
    const rows =
      dataVisualMethod === "Monthly"
        ? monthlyData
        : (weeklyShiftList || []).flatMap((w) => w?.shifts || []);
    return summarizeShifts(rows);
  }, [dataVisualMethod, monthlyData, weeklyShiftList]);

  const periodLabel =
    dataVisualMethod === "Monthly"
      ? formatMonthlyPeriodLabel(monthlyRange)
      : dataVisualMethod === "Daily"
        ? moment(selectedDay, MONTH_DATE_FMT).format("DD MMM YYYY")
      : `Week ${moment(selectedWeek, WEEK_FMT).isoWeek()}, ${moment(
          selectedWeek,
          WEEK_FMT,
        ).year()}`;

  const ViewToggle = ({ view }) => {
    const active = dataVisualMethod === view;
    return (
      <Button
        onClick={() => selectView(view)}
        disableElevation
        variant={active ? "contained" : "text"}
        sx={{
          textTransform: "none",
          borderRadius: 1.5,
          minWidth: 90,
          fontWeight: designWeights.semibold,
          bgcolor: active ? designColors.teal : "transparent",
          color: active ? "#fff" : shiftPalette.subText,
          "&:hover": { bgcolor: active ? designColors.tealDark : "#EEF0F3" },
        }}
      >
        {view}
      </Button>
    );
  };

  return (
    <Box sx={{ fontFamily: designFontFamily, p: { xs: 1.5, md: 3 }, bgcolor: shiftPalette.pageBg, minHeight: "100%" }}>
      {/* Breadcrumb */}
      <GlobalBreadcrumb
        sx={{ mb: 1 }}
        trail={[
          {
            label: "My Workspace",
            onClick: () => history.push("/admin/myzone/status"),
          },
          "Shift",
        ]}
      />

      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography sx={{ ...designType.pageTitle, color: shiftPalette.text }}>
            Shift
          </Typography>
          <Typography sx={{ ...designType.pageSubtitle, color: shiftPalette.subText }}>
            View your assigned shifts, upcoming schedule and shift-related requests.
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2.5}>
        {/* Main column */}
        <Grid item xs={12} lg={8.5}>
          <Box sx={{ ...shiftCardSx, p: 2.5 }}>
            {/* Toolbar: view toggle + period nav */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              spacing={1.5}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ display: "flex", gap: 0.5, p: 0.5, borderRadius: 2, bgcolor: "#F2F4F7" }}>
                  <ViewToggle view="Daily" />
                  <ViewToggle view="Weekly" />
                  <ViewToggle view="Monthly" />
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={1}>
                {dataVisualMethod === "Monthly" ? (
                  <ApprovalDateRangeFilter
                    startDate={monthlyRange.start}
                    endDate={monthlyRange.end}
                    dateRangeLabel={monthlyRange.label}
                    onChange={handleMonthlyDateOnChange}
                    allowFutureDate
                    minDate={minDate}
                    alwaysShowCalendars
                    sx={{
                      width: { xs: "100%", sm: 330 },
                      maxWidth: "100%",
                      justifyContent: "flex-start",
                    }}
                  />
                ) : dataVisualMethod === "Daily" ? (
                  <>
                    <TextField
                      label="Select Date"
                      size="small"
                      type="date"
                      value={selectedDay}
                      onChange={handleDailyDateOnChange}
                      sx={{ width: { xs: "100%", sm: 200 } }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-end", sm: "flex-start" }}>
                      <IconButton
                        size="small"
                        onClick={() => stepPeriod(-1)}
                        sx={{ border: `1px solid ${shiftPalette.border}` }}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => stepPeriod(1)}
                        sx={{ border: `1px solid ${shiftPalette.border}` }}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </>
                ) : (
                  <>
                    <TextField
                      label="Select Week"
                      size="small"
                      type="week"
                      value={selectedWeek}
                      onChange={handleWeeklyDateOnChange}
                      sx={{ width: { xs: "100%", sm: 200 } }}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-end", sm: "flex-start" }}>
                      <IconButton
                        size="small"
                        onClick={() => stepPeriod(-1)}
                        sx={{ border: `1px solid ${shiftPalette.border}` }}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => stepPeriod(1)}
                        sx={{ border: `1px solid ${shiftPalette.border}` }}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>

            {/* Calendar / list */}
            {dataVisualMethod === "Weekly" || dataVisualMethod === "Daily" ? (
              <Weekly loading={loading} weeklyShiftList={weeklyShiftList} listOfDate={listOfDate} />
            ) : (
              <Monthly
                loading={loading}
                monthlyData={monthlyData}
                startDate={startDate}
                endDate={endDate}
              />
            )}

            {/* Legend */}
            <Stack
              direction="row"
              spacing={2.5}
              sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}
            >
              {LEGEND.map((c) => (
                <Stack key={c.key} direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c.color }} />
                  <Typography variant="caption" sx={{ color: shiftPalette.subText }}>
                    {c.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Grid>

        {/* Side panel */}
        <Grid item xs={12} lg={3.5}>
          <ShiftSummaryPanel summary={summary} periodLabel={periodLabel} />
        </Grid>
      </Grid>
    </Box>
  );
};


export const MyShift = () => (
  <ThemeProvider theme={designTheme}>
    <CssBaseline />
    <ShiftContent />
  </ThemeProvider>
);

export default MyShift;
